// auth.service.ts - VERSIÓN LIMPIA SIN CONSOLE LOGS

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { checkUserActivatedResponseData, CiudUserAuthData, LoginDataService, UserAuthData, userDataGoogle } from './authData.model';
import Swal from 'sweetalert2';
import { env, python } from 'src/environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CookieService } from 'ngx-cookie-service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  //PROPIEDADES DEL SERVICIO  ===============================
  private readonly apiWeb: string = `${env.url_base}${env.api}`;
  private readonly apiWeb2: string = `${env.url_base}${env.api}${env.landing}${env.perfil_usuario}`;
  private readonly apiWeb3 = `${python.url_base}`;

  //mantiene el estado actual del usuario autenticado.
  private ciudUserSubject = new BehaviorSubject<CiudUserAuthData | null>(null);

  //Observable que emite el estado actual del usuario autenticado cada vez que currentUserSubject cambia.
  public ciudUser$ = this.ciudUserSubject.asObservable();

  private sessionCheckInterval: any;

  constructor(
    private http: HttpClient,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private loginDataService: LoginDataService,
    private snackbar: MatSnackBar,
    private cookieService: CookieService
  ) {
    //Recupera los datos del usuario del localStorage si existen
    // y los establece en currentUserSubject.
    const storedUser = sessionStorage.getItem('user');
    if (storedUser) {
      this.ciudUserSubject.next(JSON.parse(storedUser));
    }
  }

  // =====================================
  // 🆕 MÉTODOS DE VALIDACIÓN CROSS-ENVIRONMENT
  // =====================================

  /**
   * 🔧 Configurar entorno actual (llamado automáticamente en initializeSession)
   */
  private setCurrentEnvironment(): void {
    sessionStorage.setItem('current_environment', this.getCurrentEnvironment());
  }



  /**
   * 🔍 Obtener todas las cookies de autenticación
   */
  private getAuthCookies(): { hasAny: boolean; cookies: Record<string, string | null> } {
    const cookieNames = ['token', 'refresh_token', 'access_token', 'auth_token'];
    const cookies: Record<string, string | null> = {};
    let hasAny = false;

    for (const name of cookieNames) {
      const value = this.getCookie(name);
      cookies[name] = value;
      if (value) {
        hasAny = true;
      }
    }

    return { hasAny, cookies };
  }

  /**
   * 🔍 Obtener valor de cookie
   */
  private getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      const cookieValue = parts.pop()?.split(';').shift();
      return cookieValue || null;
    }
    return null;
  }

  /**
   * 🔍 Determinar entorno de una cookie
   */
  private getCookieEnvironment(cookieValue: string): string | null {
    try {
      const decoded = this.decodeJWT(cookieValue);
      if (decoded && decoded.iss) {
        return this.getEnvironmentFromIssuer(decoded.iss);
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * 🔍 Obtener entorno desde issuer
   */
  private getEnvironmentFromIssuer(issuer: string): string {
    const normalizedIssuer = issuer.toLowerCase();

    if (normalizedIssuer.includes('dev.')) return 'dev';
    if (normalizedIssuer.includes('pru.')) return 'pru';
    if (normalizedIssuer.includes('pre.')) return 'pre';
    if (normalizedIssuer.includes('test.')) return 'test';
    if (normalizedIssuer.includes('staging.')) return 'staging';
    if (normalizedIssuer.includes('localhost')) return 'local';

    return 'prod';
  }

  /**
   * 🔍 Detectar entorno actual
   */
  private getCurrentEnvironment(): string {
    const hostname = window.location.hostname;

    if (hostname.includes('dev.')) return 'dev';
    if (hostname.includes('pru.')) return 'pru';
    if (hostname.includes('pre.')) return 'pre';
    if (hostname.includes('test.')) return 'test';
    if (hostname.includes('staging.')) return 'staging';
    if (hostname.includes('localhost')) return 'local';

    return 'prod';
  }

  /**
   * 🧹 Limpiar solo cookies cross-environment
   */
  private cleanCrossEnvironmentCookies(): void {
    const cookiesToClear = ['token', 'refresh_token', 'access_token', 'auth_token'];
    const currentHostname = window.location.hostname;

    cookiesToClear.forEach(cookieName => {
      // Limpiar para el path actual
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;

      // Limpiar para dominios padre
      const parts = currentHostname.split('.');
      if (parts.length > 2) {
        const parentDomain = '.' + parts.slice(1).join('.');
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${parentDomain}`;
      }
    });
  }

  /**
   * 🧹 Limpiar sessionStorage específico
   */
  private clearSessionStorage(): void {
    const keysToClean = [
      'user', 'loginTime', 'data_register', 'token_register', 'loggedinUser'
    ];

    keysToClean.forEach(key => {
      if (sessionStorage.getItem(key)) {
        sessionStorage.removeItem(key);
      }
    });
  }



  checkUserActivated(email: string): Observable<checkUserActivatedResponseData> {
    const body = {
      email
    };

    return this.http.post<checkUserActivatedResponseData>(`${this.apiWeb3}/checkUuserActivated`, body, { withCredentials: true }).pipe(
      map(response => {
        try {
          if (response.success) {
            return { success: true, message: 'Usuario activado', data: response.data };
          } else {
            return { success: false, message: response.message || 'El usuario no está activado' };
          }
        } catch (error) {
          return { success: false, message: 'Hubo un error, por favor, intentelo de nuevo' };
        }
      }),
      catchError((error: HttpErrorResponse) => {
        return of({ success: false, message: 'Error en la solicitud' });
      })
    );
  }

  login2Web(email: string, password: string, fullDomain: string, recaptchaToken: string, secretKey: string): Observable<{ success: number, message: string, data?: CiudUserAuthData, token_access?: string, refresh_token?: string, is_temp_password?: boolean }> {
    const body = {
      email,
      password,
      fullDomain,
      recaptchaToken,
      secretKey
    };

    return this.http.post<{ success: number, message: string, data?: CiudUserAuthData, token_access?: string, refresh_token?: string, is_temp_password?: boolean }>(`${this.apiWeb3}/login`, body, { withCredentials: true }).pipe(
      map(response => {
        try {
          if(response.success === 1 && response.data){
            const userData = new CiudUserAuthData(response.data );

            const userWithAdditionalData = {
              ...userData,
              logGoogle: false,
              is_temp_password: response.is_temp_password
            };

            sessionStorage.setItem('user', JSON.stringify(userWithAdditionalData));

            this.loginDataService.setData(userData);
            this.ciudUserSubject.next(userData);

            const token_access = response.token_access;
            if (token_access) {
              this.setSessionFromJWT(token_access);
            }

            this.startSessionCheck();

            return { success: 1, message: 'Inicio de sesión exitoso', data: userData };
          } else if (response.success === 2) {
            return { success: 2, message: response.message || 'Credenciales pendientes de completar el proceso de registro. Por favor solicite un nuevo correo con contraseña temporal para finalizar su registro.'};
          } else if (response.success === 3) {
            return { success: 3, message: response.message || 'Contraseña incorrecta, por favor verifica tu correo para continuar con el registro desde el link enviado.'};
          } else{
            return { success: 0, message: response.message || 'El inicio de sesión no fue exitoso. Por favor, intentalo de nuevo.' };
          }
        } catch (error) {
          return { success: 0, message: 'Hubo un error, por favor, intentalo mas tarde' };
        }
      }),
      catchError((error: HttpErrorResponse) => {
        return of({ success: 0, message: error.message || 'Error de conexión' });
      })
    );
  }

  // 🔧 CORRECCIÓN PRINCIPAL: loginOAuthGoogle sin debugging
  loginOAuthGoogle(ladata: userDataGoogle, domain: string): Observable<{ success: number, message: string, data?: CiudUserAuthData, token_access?: string, refresh_token?: string }> {
    // Validar datos de entrada
    if (!ladata) {
      return of({ success: 0, message: 'Datos de usuario inválidos' });
    }

    if (!ladata.email) {
      return of({ success: 0, message: 'Email requerido' });
    }

    const body = {
      email: ladata.email,
      email_verified: ladata.email_verified,
      given_name: ladata.given_name,
      family_name: ladata.family_name,
      domain: domain
    };

    return this.http.post<{ success: number, message: string, data?: CiudUserAuthData, token_access?: string, refresh_token?: string }>(
      `${this.apiWeb3}/login_registerGoogle`,
      body,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    ).pipe(
      map(response => {
        try {
          if(response.success === 1 && response.data) {
            const userData = new CiudUserAuthData(response.data);
            // Crear un nuevo objeto combinando 'userData' con la nueva propiedad adicional
            const userWithAdditionalData = {
              ...userData,
              logGoogle: true
            };

            sessionStorage.setItem('user', JSON.stringify(userWithAdditionalData));

            this.loginDataService.setData(userData);
            this.ciudUserSubject.next(userData);

            const token_access = response.token_access;
            if (token_access) {
              this.setSessionFromJWT(token_access);
            }

            this.startSessionCheck();

            return { success: 1, message: 'Login Exitoso', data: response.data };
          } else {
            return { success: 0, message: response.message || 'El inicio de sesion no fue exitoso, por favor intentelo más tarde' };
          }
        } catch (error) {
          return { success: 0, message: 'Hubo un error procesando la respuesta, por favor, intentelo de nuevo' };
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // Mensaje de error más específico según el status
        let errorMessage = 'Error de conexión con el servidor';
        if (error.status === 0) {
          errorMessage = 'No se pudo conectar con el servidor. Verifica tu conexión a internet.';
        } else if (error.status >= 500) {
          errorMessage = 'Error interno del servidor. Intenta nuevamente en unos momentos.';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        return of({ success: 0, message: errorMessage });
      })
    );
  }

  /**
   * Método para renovar el token de acceso.
   * Este método utiliza la cookie `refresh_token`.
   */
  refreshAccessToken(): Observable<{ success: boolean; token_access?: string, respRefreshData?: {UserData?: CiudUserAuthData, success: boolean} }> {
    const domain = sessionStorage.getItem('domain') || '';

    const body = {
      domain
    };

    return this.http.post<{ success: boolean; token_access?: string, respRefreshData?: {UserData?: CiudUserAuthData, success: boolean} }>(
      `${this.apiWeb3}/refresh_token`,
      body,
      { withCredentials: true } // Permitir cookies
    ).pipe(
      map((response) => {
        if (response.token_access) {
          this.setSessionFromJWT(response.token_access); // Actualiza el token en localStorage
          return { success: true, token_access: response.token_access, respRefreshData: response.respRefreshData };
        }
        this.stopSessionCheck();
        return { success: false };
      }),
      catchError((error) => {
        return of({ success: false });
      })
    );
  }

  performProtectedRequest<T>(
    requestFn: () => Observable<T>
  ): Observable<T> {
    // Realiza la solicitud inicial
    return requestFn().pipe(
      catchError((error) => {
        if (error.status === 401) {
          return this.refreshAccessToken().pipe(
            switchMap((response) => {
              if (response.success) {
                // Reintenta la solicitud original
                return requestFn();
              } else {
                this.logout();
                return throwError(() => new Error('No autorizado'));
              }
            })
          );
        }
        // Propaga otros errores
        return throwError(() => error);
      })
    );
  }

  //METODOS COMUNES PARA EL SERVICIO ===============================
  /**
   * Inicializa la sesión verificando la existencia de la cookie de sesión.
   * INCLUYE validación cross-environment automática
   */
  initializeSession(): void {
    // 🔧 PASO 1: Configurar entorno actual
    this.setCurrentEnvironment();

    // 🔧 PASO 2: Validación cross-environment
    const shouldContinue = this.validateCrossEnvironment();
    if (!shouldContinue) {
      return; // Se detectó cross-environment, sesión bloqueada
    }

    // 🔧 PASO 3: Lógica original de inicialización de sesión
    const storedUser = sessionStorage.getItem('user');
    const hasTokenCookie = this.checkCookieExists('token');
    const hasRefreshTokenCookie = this.checkCookieExists('refresh_token');

    // Si tenemos cookies pero no user en sessionStorage, probablemente venimos de landing-pqr
    if ((hasTokenCookie && hasRefreshTokenCookie) && !storedUser) {
      // Intentamos renovar el token para obtener los datos del usuario
      this.refreshAccessToken().subscribe({
        next: (response) => {
          if (response.success && response?.respRefreshData?.UserData) {
            const userData = new CiudUserAuthData(response.respRefreshData.UserData);

            // Guardamos datos de usuario y actualizamos el estado
            const userWithAdditionalData = {
              ...userData,
              logGoogle: false // Asumimos login normal por defecto
            };

            sessionStorage.setItem('user', JSON.stringify(userWithAdditionalData));
            this.ciudUserSubject.next(userData);
            this.loginDataService.setData(userData);

            // Iniciar chequeo periódico de sesión
            this.startSessionCheck();

            // Manejar redirección
            this.handleRedirection();
          } else {
            this.clearInvalidSession();
          }
        },
        error: (err) => {
          this.clearInvalidSession();
        }
      });
    }
    // Si ya tenemos datos de usuario en sessionStorage, verificamos que las cookies sean válidas
    else if (storedUser) {
      if (hasTokenCookie && hasRefreshTokenCookie) {
        // Todo parece estar en orden, verificamos que el token sigue siendo válido
        this.refreshAccessToken().subscribe({
          next: (response) => {
            if (response.success) {
              // Actualizar datos de usuario si recibimos nuevos datos
              if (response?.respRefreshData?.UserData) {
                const userData = new CiudUserAuthData(response.respRefreshData.UserData);

                // Mantener el estado de login de Google
                const currentUser = JSON.parse(storedUser);
                const userWithAdditionalData = {
                  ...userData,
                  logGoogle: currentUser.logGoogle || false
                };

                sessionStorage.setItem('user', JSON.stringify(userWithAdditionalData));
                this.ciudUserSubject.next(userData);
                this.loginDataService.setData(userData);
              }

              this.startSessionCheck();
              this.handleRedirection();
            } else {
              this.clearInvalidSession();
            }
          },
          error: () => {
            this.clearInvalidSession();
          }
        });
      } else {
        this.clearInvalidSession();
      }
    }
  }

  /**
   * 🔧 Validación cross-environment integrada (versión síncrona)
   */
  private validateCrossEnvironment(): boolean {
    const currentEnv = this.getCurrentEnvironment();

    // Verificar si hay cookies de autenticación
    const authCookies = this.getAuthCookies();

    if (!authCookies.hasAny) {
      return true; // Permitir continuar
    }

    // 🔍 CLAVE: Solo validar ENTORNO, no URLs específicas
    for (const [cookieName, cookieValue] of Object.entries(authCookies.cookies)) {
      if (cookieValue) {
        const cookieEnv = this.getCookieEnvironment(cookieValue);

        // 🚨 SOLO bloquear si son ENTORNOS diferentes
        if (cookieEnv && cookieEnv !== currentEnv) {
          this.cleanCrossEnvironmentCookies();
          this.clearSessionStorage();
          return false; // ❌ Bloquear inicialización
        }
      }
    }

    // ✅ Mismos entornos o cookies válidas
    return true;
  }

  restoreSessionFromCookies(): Observable<boolean> {
    return this.refreshAccessToken().pipe(
      map((response) => {
        if (response.success && response?.respRefreshData?.UserData) {
          const userData = new CiudUserAuthData(response.respRefreshData.UserData);

          // Guardamos datos de usuario y actualizamos el estado
          const userWithAdditionalData = {
            ...userData,
            logGoogle: false // Asumimos login normal por defecto
          };

          sessionStorage.setItem('user', JSON.stringify(userWithAdditionalData));
          this.ciudUserSubject.next(userData);
          this.loginDataService.setData(userData);

          // Iniciar chequeo periódico de sesión
          this.startSessionCheck();

          return true;
        } else {
          this.clearInvalidSession();
          return false;
        }
      }),
      catchError(() => {
        this.clearInvalidSession();
        return of(false);
      })
    );
  }

  /**
   * Maneja la redirección después de restaurar la sesión
   */
  private handleRedirection(): void {
    const redirectUrl = sessionStorage.getItem('redirectUrl');
    const currentRoute = this.router.url;

    // Solo redirigir si no estamos ya en una ruta protegida
    if (currentRoute === '/' || currentRoute === '/login') {
      if (redirectUrl && redirectUrl.startsWith('/')) {
        sessionStorage.removeItem('redirectUrl');

        setTimeout(() => {
          this.router.navigateByUrl(redirectUrl).catch(() => {
            this.router.navigate(['/home/mis-predios']);
          });
        }, 0);
      } else {
        setTimeout(() => {
          this.router.navigate(['/home/mis-predios']);
        }, 0);
      }
    }
  }

  /**
   * Limpia los datos de sesión inválidos
   */
  private clearInvalidSession(): void {
    sessionStorage.removeItem('user');
    this.ciudUserSubject.next(null);
    this.stopSessionCheck();

    // No redireccionar automáticamente aquí, dejar que el guard lo haga
  }

  startSessionCheck() {
    this.stopSessionCheck(); // Asegura que no haya intervalos duplicados
    this.sessionCheckInterval = setInterval(() => {
      if (!this.isLoggedIn()) {
          this.logout(); // Cerrar sesión automáticamente si ya no está logueado
      }
    }, 60000); // Cada 60 segundos
  }

  // Método para detener el chequeo de sesión
  stopSessionCheck() {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
  }

  /**
   * Establece los datos del token y actualiza la sesión en el almacenamiento local.
   */
  private setSessionFromJWT(jwt: string): void {
    const decodedToken = this.decodeJWT(jwt);
    if (decodedToken) {
      const expirationTime = decodedToken.exp * 1000; // Convertir a milisegundos
      sessionStorage.setItem('loginTime',  JSON.stringify({ loginTime: new Date().getTime(), expirationTime })
      );
    }
  }

  /**
   * Decodifica un JWT para obtener los datos.
   */
  private decodeJWT(token: string): any {
    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload));
    } catch (e) {
      return null;
    }
  }

  /**
   * Devuelve si el usuario está autenticado.
   */
  isLoggedIn(): boolean {
    const userExists = !!sessionStorage.getItem('user'); // Verifica si hay un usuario en sessionStorage

    if (!userExists) {
      return false;
    }

    // Verificar si las cookies 'token' y 'refresh_token' existen
    const hasToken = this.checkCookieExists('token');
    const hasRefreshToken = this.checkCookieExists('refresh_token');

    if (!hasToken || !hasRefreshToken) {
      return false;
    }

    // Verificar si el token sigue siendo válido
    const loginTime = JSON.parse(sessionStorage.getItem('loginTime') || '{}');
    const currentTime = new Date().getTime();

    if (loginTime && currentTime > loginTime.expirationTime) {
      this.logout(); // Si el token ha expirado, cerrar sesión
      return false;
    }

    return true;
  }

  /**
   * Método para cerrar sesión y limpiar los datos.
   */
  logout(): void {
    const domain = sessionStorage.getItem('domain') || '';
    const body = { domain };

    // Primero limpia las sesiones locales
    this.stopSessionCheck();
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('loginTime');
    this.ciudUserSubject.next(null);

    // Luego llama al endpoint para eliminar cookies del servidor
    this.http.post(`${this.apiWeb3}/logout`, body, { withCredentials: true }).subscribe({
      next: () => {
        // Usa una navegación más controlada
        window.location.href = '/login'; // Más directo que reload() y asegura ir a login
      },
      error: (err) => {
        window.location.href = '/login';
      },
    });
  }

  /**
   * Devuelve el usuario actual.
   */
  getCurrentUser(): CiudUserAuthData | null {
    return this.ciudUserSubject.value;
  }

  updateUserProfile(userData: CiudUserAuthData) {
    this.ciudUserSubject.next(userData);
    sessionStorage.setItem('user', JSON.stringify(userData));
  }

  checkCookieExists(cookieName: string): boolean {
    const cookie = this.cookieService.get(cookieName);
    return !!cookie;  // Retorna true si la cookie existe, de lo contrario false
  }

  // =====================================
  // 🔍 MÉTODOS DE DEBUGGING
  // =====================================

  /**
   * 🔍 Debug del estado de navegación
   */
  debugNavigationState(): void {
    // Método mantenido para compatibilidad pero sin logs
    const authCookies = this.getAuthCookies();
    Object.entries(authCookies.cookies).forEach(([name, value]) => {
      if (value) {
        const env = this.getCookieEnvironment(value);
        // Debug info available but not logged
      }
    });
  }
}
